import axios from 'axios';
import { CORE_API_BASE_URL, APP_KEY } from '../constants/index';

// Credenciamento 100% no Social Miror Core. Este módulo concentra o acesso ao
// Core (login/refresh/me/sso) e o armazenamento de tokens + empresa ativa.

const ACCESS_KEY = 'glivo.access';
const REFRESH_KEY = 'glivo.refresh';
const COMPANY_KEY = 'glivo.company_id';

export interface CoreMembership {
  id: string;
  company_id: string;
  role: string;
}

export const tokenStore = {
  getAccess: () => localStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  getCompanyId: () => localStorage.getItem(COMPANY_KEY),
  setTokens: (access: string, refresh?: string | null) => {
    if (access) localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  setCompanyId: (id: string | null) => {
    if (id) localStorage.setItem(COMPANY_KEY, id);
    else localStorage.removeItem(COMPANY_KEY);
  },
  clear: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(COMPANY_KEY);
    localStorage.removeItem('user');
  },
};

const coreApi = axios.create({
  baseURL: CORE_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

interface TokenPair {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
}

export async function coreLogin(email: string, password: string): Promise<TokenPair> {
  const { data } = await coreApi.post('/auth/login', { email, password });
  return data;
}

export async function coreLoginPin(email: string, pin: string): Promise<TokenPair> {
  const { data } = await coreApi.post('/auth/login-pin', { email, pin });
  return data;
}

export async function coreRefresh(refresh: string): Promise<TokenPair> {
  const { data } = await coreApi.post('/auth/refresh', { refresh_token: refresh });
  return data;
}

// Refresh single-flight COMPARTILHADO entre todos os clients (api.ts + coreAuthed).
// O Core rotaciona o refresh token, então dois refresh concorrentes invalidariam
// um ao outro e derrubariam a sessão — por isso um único voo de refresh global.
// Persiste os tokens novos no store e devolve o par.
let refreshing: Promise<TokenPair> | null = null;

export function refreshSessionOnce(): Promise<TokenPair> {
  if (!refreshing) {
    const refresh = tokenStore.getRefresh();
    if (!refresh) return Promise.reject(new Error('no_refresh_token'));
    refreshing = coreRefresh(refresh)
      .then((data) => {
        tokenStore.setTokens(data.access_token, data.refresh_token);
        return data;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

export async function coreMemberships(access: string): Promise<CoreMembership[]> {
  const { data } = await coreApi.get('/me/memberships', {
    headers: { Authorization: `Bearer ${access}` },
  });
  return Array.isArray(data) ? data : data.items || [];
}

// Valida o code de SSO vindo do hub (Social) e devolve a sessão completa.
export async function coreValidateSso(code: string): Promise<{
  access_token: string;
  refresh_token: string;
  active_company_id?: string;
}> {
  const { data } = await coreApi.post(
    '/sso/validate-code',
    { code, app_key: APP_KEY },
    { headers: { 'X-App-Key': APP_KEY } },
  );
  return data;
}

export async function coreLogout(refresh: string | null): Promise<void> {
  if (!refresh) return;
  try {
    await coreApi.post('/auth/logout', { refresh_token: refresh });
  } catch {
    /* logout é best-effort */
  }
}
